<template>
  <div class="mobileViewStyle calendar-execute">
    <!-- 日程执行（企业微信手机端）：记录这条日程办没办完、花了多久、有什么要说的 -->
    <div class="detail-body">
      <template v-if="row.id">
        <!-- 日程信息只读：要改标题、日期、关联客户请走「编辑」，这里只落执行结果 -->
        <list-card :label="row.title">
          <template #desc>
            <span class="type-tag">{{ typeName(row.calendarType) }}</span>
          </template>
          <data-column label="归属日期">{{ dateOnly(row.goalDate) }}</data-column>
          <data-column label="类型">{{ typeName(row.calendarType) }}</data-column>
          <data-column v-if="row.remark" label="日程备注">{{ row.remark }}</data-column>
        </list-card>

        <list-card label="执行结果" type="form">
          <data-input label="状态">
            <mobile-select class="row-select" :clearable="false" v-model="form.kpiStatus"
                           :placeholder="statusLocked ? '已完成，不能再改状态' : '请选择状态'">
              <mobile-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
            </mobile-select>
          </data-input>
          <data-input label="备注" v-model="form.result" desc="" placeholder="选填，本次执行的情况、结果说明"/>
          <data-input label="消耗工时(h)" v-model="form.hours" desc="" placeholder="0"/>
        </list-card>
      </template>
      <div v-else-if="!loading" class="state-tip">没有找到这条日程</div>
    </div>

    <div class="detail-footer" v-if="row.id">
      <mobile-button @click="cancel">取消</mobile-button>
      <mobile-button el-type="primary" @click="saveData">保存</mobile-button>
    </div>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataInput from "@/components/qywx/data/DataInput.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import MobileSelect from "@/components/qywx/data/MobileSelect.vue";
import MobileOption from "@/components/qywx/data/MobileOption.vue";
import MobileButton from "@/components/qywx/button/MobileButton.vue";
import calendarApi from "@/api/dz-crm/kpi/calendarApi";
import {typeName, dateOnly} from "@/views/h5/crm/kpi/kpiCalendarDictionary";

/** 状态：未完成/延期(50) 与 已完成(100)。日程是「做没做完」，没有「待办」这一档可选 */
const STATUS_DONE = 100;
const STATUS_DELAY = 50;
const EXECUTE_STATUS_OPTIONS = [
  {value: STATUS_DELAY, label: "未完成/延期"},
  {value: STATUS_DONE, label: "已完成"},
];

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "KpiCalendarExecute",
  components: {
    MobileButton, MobileOption, MobileSelect, DataColumn, DataInput, ListCard
  },
  data() {
    return {
      loading: true,
      employeeId: "",
      goalDate: "",
      row: {},
      form: {},
      statusOptions: EXECUTE_STATUS_OPTIONS,
      pageTitle: "",
    };
  },
  computed: {
    /** 已完成是终态：状态不能再改（备注与工时仍可补记） */
    statusLocked() {
      return Number(this.row.kpiStatus) === STATUS_DONE;
    }
  },
  created() {
    this.pageTitle = document.title;
    document.title = "日程执行";
    this.employeeId = this.route.query.employeeId || "";
    this.goalDate = this.route.query.date || "";
    this.findById();
  },
  unmounted() {
    document.title = this.pageTitle;
  },
  methods: {
    typeName(type) {
      return typeName(type);
    },
    dateOnly(value) {
      return dateOnly(value);
    },
    /**
     * 日历行没有「按 id 查一条」的接口，按员工 + 日期把当天拉回来再挑出这一条。
     * 员工与日期由日历页带过来。
     */
    findById() {
      const id = this.route.query.id;
      if (!id || !this.employeeId || !this.goalDate) {
        this.loading = false;
        return;
      }
      // queryDay 返回的是当天日历行数组，不能按 {calendarList: [...]} 取
      calendarApi.queryDay(this.employeeId, this.goalDate).then(list => {
        this.row = (list || []).find(item => item.id === id) || {};
        // 默认沿用现有状态（已完成 / 未完成延期）；还是待办或没有状态时默认「已完成」——
        // 会点「执行」多半就是办完了，选错也看得见，比默认成「延期」把日程顺延掉安全
        const current = Number(this.row.kpiStatus);
        const defaultStatus = current === STATUS_DONE || current === STATUS_DELAY ? current : STATUS_DONE;
        this.form = {
          kpiStatus: defaultStatus,
          result: this.row.result || "",
          hours: this.row.hours,
        };
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      });
    },
    cancel() {
      this.router.back();
    },
    saveData() {
      const status = Number(this.form.kpiStatus);
      const doSave = () => {
        // 只同步这一行：备注落到 result、工时落到 hours，状态按所选
        const row = Object.assign({}, this.row, {
          kpiStatus: status,
          result: this.form.result,
          hours: Number(this.form.hours) || 0,
        });
        return calendarApi.markDone({
          employeeId: this.employeeId,
          goalDate: this.goalDate,
          // 不带日志文本：日志由「写日志」页单独维护
          result: "",
          calendarIds: [this.row.id],
          calendarList: [row],
        });
      };
      // 标记「未完成/延期」会把该日程当天及之后的行整体顺延，有副作用，先二次确认
      if (status === STATUS_DELAY && Number(this.row.kpiStatus) !== STATUS_DELAY) {
        this.$confirm("标记为「未完成/延期」后，该日程将整体顺延到下一个工作日，确认吗？", "提示", {type: "warning"})
            .then(doSave)
            .then(() => this.router.back())
            .catch(() => {
            });
        return;
      }
      doSave().then(() => this.router.back());
    }
  }
};
</script>

<style lang="scss" scoped>
.calendar-execute{
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--yq-background-color);
  .detail-body{
    flex: 1;
    overflow-y: auto;
    padding: 10px;
  }
  .detail-footer{
    flex: none;
    display: flex;
    gap: 10px;
    padding: 10px;
    background: #FFF;
    box-shadow: 0 -1px 4px var(--yq-shadow);
    :deep(.mobile-button){
      flex: 1;
    }
  }
  .state-tip{
    padding: 60px 20px;
    text-align: center;
    color: var(--yq-font-color-desc);
  }
  // 标题行右侧的类型标签 + 名称允许折两行
  :deep(.card-title){
    height: auto;
    min-height: 60px;
    .name{
      white-space: normal;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      line-height: 1.3;
    }
    .desc{
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }
  .type-tag{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  // 状态选择放在 data-input 行里：去掉自带边框/箭头，字号跟随整行
  :deep(.row-select .select-area){
    width: auto;
    box-shadow: none;
    padding: 0;
    background: transparent;
    font-size: inherit;
    color: var(--yq-font-color-base);
    .value{
      flex: none;
      width: auto;
      max-width: 220px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      text-align: right;
      &.placeholder{
        color: var(--yq-font-color-desc);
      }
    }
    .arrow-down{
      display: none;
    }
  }
}
</style>
